import { useEffect, useState } from "react";
import { api, fmtBRL } from "@/lib/api";
import { MagnifyingGlass, Trash, X, CheckCircle, UserCircle } from "@phosphor-icons/react";

export default function ManualOrderModal({ onClose, onCreated }) {
  const [marketplace, setMarketplace] = useState("Manual");
  const [category, setCategory] = useState("Geral");
  const [exitDate, setExitDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  });
  const [customer_id, setCustomerId] = useState(null);
  const [customer_name, setCustomerName] = useState("");
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerResults, setCustomerResults] = useState([]);
  const [showCustomerList, setShowCustomerList] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (productSearch.length === 0) { setProducts([]); return; }
      try {
        const { data } = await api.get("/products", { params: { search: productSearch, only_active: true } });
        setProducts(data.slice(0, 12));
      } catch (e) { console.warn("Manual order product search:", e?.response?.status || e?.message); }
    }, 250);
    return () => clearTimeout(t);
  }, [productSearch]);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (customerSearch.length === 0) { setCustomerResults([]); return; }
      try {
        const { data } = await api.get("/customers", { params: { search: customerSearch } });
        setCustomerResults(data.slice(0, 8));
        setShowCustomerList(true);
      } catch (e) { console.warn("Manual order customer search:", e?.response?.status || e?.message); }
    }, 250);
    return () => clearTimeout(t);
  }, [customerSearch]);

  const addItem = (p) => {
    setItems((prev) => {
      const exists = prev.find((it) => it.sku === p.sku);
      if (exists) {
        return prev.map((it) => it.sku === p.sku ? { ...it, qty: it.qty + 1 } : it);
      }
      return [...prev, {
        sku: p.sku,
        name: p.name,
        qty: 1,
        unit_price: p.price || 0,
        units_per_unit: p.sku_parsed?.units_per_unit || 1,
        type: p.sku_parsed?.type || "Genérico",
      }];
    });
    setProductSearch("");
  };

  const updateQty = (idx, qty) => {
    const q = Math.max(1, parseInt(qty) || 1);
    setItems((prev) => prev.map((it, i) => i === idx ? { ...it, qty: q } : it));
  };

  const updatePrice = (idx, price) => {
    setItems((prev) => prev.map((it, i) => i === idx ? { ...it, unit_price: parseFloat(price) || 0 } : it));
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const totalCups = items.reduce((s, it) => s + (it.qty * (it.units_per_unit || 1)), 0);
  const totalValue = items.reduce((s, it) => s + (it.qty * (it.unit_price || 0)), 0);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!customer_name) { setError("Cliente é obrigatório"); return; }
    if (items.length === 0) { setError("Adicione pelo menos 1 item"); return; }
    try {
      const { data } = await api.post("/orders/manual", {
        customer: customer_name,
        customer_id,
        marketplace,
        category,
        exit_date: exitDate || null,
        items: items.map((it) => ({ sku: it.sku, name: it.name, qty: it.qty, unit_price: it.unit_price })),
      });
      onCreated(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro ao criar pedido");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 md:p-6 overflow-y-auto" onClick={onClose}>
      <div className="glass max-w-5xl w-full p-6 md:p-8 my-4" onClick={(e) => e.stopPropagation()} data-testid="manual-order-modal">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="overline text-zinc-500">CRIAÇÃO MANUAL</div>
            <h2 className="font-head text-2xl md:text-3xl font-black">Novo Pedido</h2>
          </div>
          <button onClick={onClose}><X size={22} /></button>
        </div>

        <form onSubmit={submit} className="space-y-5">
          <div className="border border-black bg-yellow-50 px-3 py-2 text-xs flex items-start gap-2" data-testid="manual-auto-number-note">
            <span className="font-mono font-bold text-sm">M####</span>
            <span className="text-zinc-700">O número deste pedido será gerado automaticamente pelo sistema (formato <strong className="font-mono">M0001, M0002, ...</strong>) e não pode ser alterado depois. Números iniciados por <strong>M</strong> são exclusivos de pedidos criados aqui.</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="label">Marketplace</label>
              <input className="input" value={marketplace} onChange={(e) => setMarketplace(e.target.value)} data-testid="manual-marketplace" />
            </div>
            <div>
              <label className="label">Categoria</label>
              <input className="input" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="manual-category" />
            </div>
            <div>
              <label className="label">Data limite de envio</label>
              <input type="date" className="input font-mono" value={exitDate} onChange={(e) => setExitDate(e.target.value)} data-testid="manual-exit-date" />
              <p className="text-[10px] text-zinc-500 mt-1">Define a urgência no Kanban/Painel. Padrão: 7 dias.</p>
            </div>
            <div className="relative">
              <label className="label">Cliente *</label>
              <input
                className="input"
                placeholder="Buscar ou digitar nome"
                value={customer_name}
                onChange={(e) => { setCustomerName(e.target.value); setCustomerSearch(e.target.value); setCustomerId(null); }}
                onFocus={() => customerSearch && setShowCustomerList(true)}
                onBlur={() => setTimeout(() => setShowCustomerList(false), 200)}
                data-testid="manual-customer"
              />
              {showCustomerList && customerResults.length > 0 && (
                <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-black max-h-60 overflow-y-auto">
                  {customerResults.map((c) => (
                    <button type="button" key={c.id} className="w-full text-left p-3 hover:bg-zinc-100 border-b border-zinc-100 flex items-center gap-2" onMouseDown={(e) => { e.preventDefault(); setCustomerName(c.name); setCustomerId(c.id); setShowCustomerList(false); }} data-testid={`pick-customer-${c.id}`}>
                      <UserCircle size={18} />
                      <div>
                        <div className="font-semibold text-sm">{c.name}</div>
                        <div className="text-xs text-zinc-500 font-mono">{c.document || c.email || "—"}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="border border-black p-4 bg-white">
            <div className="flex items-center justify-between mb-3">
              <div className="overline">ADICIONAR PRODUTOS</div>
              <div className="text-xs text-zinc-500">SKU define cópos: <span className="font-mono">POP050</span> = 50, <span className="font-mono">TWS100</span> = 100, etc.</div>
            </div>
            <div className="relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input className="input pl-10" placeholder="Buscar produto por SKU ou nome…" value={productSearch} onChange={(e) => setProductSearch(e.target.value)} data-testid="product-search" />
            </div>
            {products.length > 0 && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                {products.map((p) => (
                  <button type="button" key={p.id} className="border border-zinc-200 hover:border-black p-3 text-left flex items-center justify-between gap-3" onClick={() => addItem(p)} data-testid={`pick-product-${p.sku}`}>
                    <div className="min-w-0">
                      <div className="font-mono font-bold text-sm">{p.sku}</div>
                      <div className="text-xs text-zinc-600 truncate">{p.name}</div>
                      <div className="text-[10px] text-[#002FA7] mt-0.5">{p.sku_parsed?.type} · {p.sku_parsed?.units_per_unit} un</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm">{fmtBRL(p.price)}</div>
                      <div className="text-[10px] text-zinc-400">Add +</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="border border-black bg-white">
            <div className="border-b border-black p-3 flex items-center justify-between">
              <div className="overline">ITENS DO PEDIDO ({items.length})</div>
              <div className="text-xs text-zinc-500">Auto-calculado: <span className="font-mono font-bold text-black">{totalCups} copos</span> · <span className="font-mono font-bold text-[#00A300]">{fmtBRL(totalValue)}</span></div>
            </div>
            {items.length === 0 ? (
              <div className="p-8 text-center text-zinc-400 text-sm" data-testid="empty-items">Nenhum item ainda. Busque um produto acima.</div>
            ) : (
              <table className="table">
                <thead><tr><th>SKU</th><th>Produto</th><th>Tipo · Un/SKU</th><th className="text-center">Qtd</th><th className="text-right">Preço Un</th><th className="text-right">Total</th><th className="text-center">Copos</th><th></th></tr></thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={it.sku || `mo-${i}`} data-testid={`order-item-${i}`}>
                      <td className="font-mono font-semibold">{it.sku}</td>
                      <td className="max-w-xs truncate">{it.name}</td>
                      <td className="text-xs text-zinc-500"><span className="font-bold">{it.type}</span> · {it.units_per_unit} un</td>
                      <td className="text-center"><input type="number" min="1" className="input w-20 text-center" value={it.qty} onChange={(e) => updateQty(i, e.target.value)} data-testid={`item-qty-${i}`} /></td>
                      <td className="text-right"><input type="number" step="0.01" className="input w-28 text-right font-mono" value={it.unit_price} onChange={(e) => updatePrice(i, e.target.value)} /></td>
                      <td className="text-right font-mono font-semibold">{fmtBRL(it.qty * it.unit_price)}</td>
                      <td className="text-center font-mono font-bold text-[#E60000]">{it.qty * (it.units_per_unit || 1)}</td>
                      <td><button type="button" onClick={() => removeItem(i)} className="text-red-500" data-testid={`remove-item-${i}`}><Trash size={14} /></button></td>
                    </tr>
                  ))}
                  <tr className="bg-black text-white">
                    <td colSpan={5} className="text-right font-bold uppercase tracking-widest text-xs">TOTAL</td>
                    <td className="text-right font-mono font-bold">{fmtBRL(totalValue)}</td>
                    <td className="text-center font-mono font-bold text-[#FFC000]">{totalCups}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>

          {error && <div className="border border-[#E60000] bg-red-50 text-[#E60000] text-sm px-3 py-2">{error}</div>}

          <div className="flex gap-3 pt-2">
            <button type="button" className="btn btn-ghost flex-1" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn flex-1" disabled={items.length === 0 || !customer_name} data-testid="manual-submit"><CheckCircle size={16} weight="bold" /> Criar Pedido</button>
          </div>
        </form>
      </div>
    </div>
  );
}
